import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Trophy, Globe, Hash, Shield, Store, Gem, Swords } from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import { Avatar, RankBadge, SkeletonList, Stars, SectionHead } from '../components/ui';

const METRICS = [['points', '💎 Points'], ['elo', '⚔️ ELO'], ['level', '⭐ Level'], ['wins', '🏆 Wins'], ['shares', '📢 Shares']];
const PERIODS = [['all', 'All time'], ['month', 'This month'], ['week', 'This week']];

export default function Leaderboard() {
  const { city, guild } = useAuth();
  const [board, setBoard] = useState('heroes');
  const [scope, setScope] = useState('global');
  const [metric, setMetric] = useState('points');
  const [period, setPeriod] = useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['lb', scope, metric, period],
    queryFn: () => api.get('/leaderboard', { params: { scope, metric, period, limit: 60 } }).then(r => r.data),
    enabled: board === 'heroes',
  });
  const { data: guilds } = useQuery({ queryKey: ['lbGuilds'], queryFn: () => api.get('/leaderboard/guilds').then(r => r.data), enabled: board === 'guilds' });
  const { data: shops } = useQuery({ queryKey: ['lbShops'], queryFn: () => api.get('/leaderboard/shops').then(r => r.data), enabled: board === 'shops' });
  const { data: cities } = useQuery({ queryKey: ['lbCities'], queryFn: () => api.get('/leaderboard/cities').then(r => r.data), enabled: board === 'cities' });

  const scopes = [
    { k: 'global', l: '🌍 Global', icon: Globe },
    ...(city ? [{ k: `city:${city.handle}`, l: city.handle, icon: Hash }] : []),
    { k: 'country:@romania', l: '@romania', icon: Globe },
    ...(guild ? [{ k: `guild:${guild.handle}`, l: `!${guild.tag}`, icon: Shield }] : []),
  ];

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="card p-5 bg-gradient-to-br from-amber-400 via-brand-500 to-grape-600 text-white">
        <Trophy size={26} className="mb-2" />
        <h1 className="font-display text-2xl font-extrabold">Leaderboards</h1>
        <p className="text-white/85 text-sm">Compete in your city, your country, your guild.</p>
      </div>

      <div className="flex gap-1 bg-white p-1 rounded-xl border border-ink-100 overflow-x-auto no-scrollbar">
        {[['heroes', '🦸 Heroes'], ['guilds', '🛡️ Guilds'], ['shops', '🏪 Shops'], ['cities', '🏙️ Cities']].map(([k, l]) => (
          <button key={k} onClick={() => setBoard(k)}
            className={`flex-1 min-w-[85px] py-2 rounded-lg text-xs font-bold transition ${board === k ? 'bg-ink-900 text-white' : 'text-ink-500'}`}>{l}</button>))}
      </div>

      {board === 'heroes' && (
        <>
          <div className="space-y-2">
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
              {scopes.map(s => (
                <button key={s.k} onClick={() => setScope(s.k)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition
                    ${scope === s.k ? 'bg-brand-500 text-white' : 'bg-white border border-ink-200 text-ink-500'}`}>{s.l}</button>))}
            </div>
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
              {METRICS.map(([k, l]) => (
                <button key={k} onClick={() => setMetric(k)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition
                    ${metric === k ? 'bg-ink-900 text-white' : 'bg-white border border-ink-200 text-ink-500'}`}>{l}</button>))}
            </div>
            {metric === 'points' && (
              <div className="flex gap-1.5">
                {PERIODS.map(([k, l]) => (
                  <button key={k} onClick={() => setPeriod(k)}
                    className={`px-3 py-1 rounded-lg text-[11px] font-bold transition
                      ${period === k ? 'bg-grape-500 text-white' : 'bg-white border border-ink-200 text-ink-500'}`}>{l}</button>))}
              </div>)}
          </div>

          {isLoading ? <SkeletonList n={8} h="h-16" /> : (
            <div className="space-y-2">
              {data?.rows?.slice(0, 3).map(u => <PodiumRow key={u.id} u={u} />)}
              <div className="card p-2 space-y-1">
                {data?.rows?.slice(3).map(u => (
                  <Link key={u.id} to={`/u/${u.username}`} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-ink-50">
                    <RankBadge rank={u.rank} />
                    <Avatar src={u.image_url || u.avatar_url} name={u.username} size={38} square />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="font-bold text-sm truncate">{u.hero_name || u.username}</p>
                        {u.guild_tag && <span className="chip-grape">{u.guild_tag}</span>}
                      </div>
                      <p className="text-[11px] text-ink-400 font-semibold">
                        @{u.username} · L{u.level} {u.city_handle && `· ${u.city_handle}`}
                      </p>
                    </div>
                    <span className="font-black text-sm text-brand-600">{compact(u.score)}</span>
                  </Link>))}
              </div>
            </div>)}
        </>
      )}

      {board === 'guilds' && (
        <div className="card p-2 space-y-1">
          {!guilds ? <SkeletonList n={6} h="h-16" /> : guilds.map(g => (
            <Link key={g.id} to={`/guild/${encodeURIComponent(g.handle)}`} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-ink-50">
              <RankBadge rank={g.rank} />
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-grape-500 to-grape-700 grid place-items-center text-white font-black text-xs">
                {g.tag}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate">{g.name}</p>
                <p className="text-[11px] text-ink-400 font-semibold">{g.members_count} members · ELO {g.guild_elo}</p>
              </div>
              <span className="font-black text-sm text-grape-600">{compact(g.guild_points)}</span>
            </Link>))}
        </div>
      )}

      {board === 'shops' && (
        <div className="card p-2 space-y-1">
          {!shops ? <SkeletonList n={6} h="h-16" /> : shops.map(s => (
            <Link key={s.id} to={`/shop/${s.slug}`} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-ink-50">
              <RankBadge rank={s.rank} />
              <Avatar src={s.logo_url} name={s.name} size={38} square />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate">{s.name}</p>
                <div className="flex items-center gap-1.5">
                  <Stars value={s.rating_avg} size={10} />
                  {s.city_handle && <span className="text-[11px] text-brand-600 font-bold">{s.city_handle}</span>}
                </div>
              </div>
              <div className="text-right">
                <p className="font-black text-sm">{compact(s.community)}</p>
                <p className="text-[10px] text-ink-400 font-bold">CUSTOMERS</p>
              </div>
            </Link>))}
        </div>
      )}

      {board === 'cities' && (
        <div className="card p-2 space-y-1">
          {!cities ? <SkeletonList n={6} h="h-16" /> : cities.map(c => (
            <Link key={c.id} to={`/g/${encodeURIComponent(c.handle)}`} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-ink-50">
              <RankBadge rank={c.rank} />
              <div className="w-10 h-10 rounded-xl bg-brand-100 text-brand-600 grid place-items-center"><Hash size={18} /></div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm">{c.handle}</p>
                <p className="text-[11px] text-ink-400 font-semibold">{compact(c.members_count)} members · {c.shops} shops</p>
              </div>
              <span className="font-black text-sm text-brand-600">{compact(c.total_points)}💎</span>
            </Link>))}
        </div>
      )}
    </div>
  );
}

function PodiumRow({ u }) {
  const bg = { 1: 'from-amber-400 to-amber-600', 2: 'from-slate-300 to-slate-500', 3: 'from-orange-400 to-orange-600' }[u.rank];
  return (
    <Link to={`/u/${u.username}`} className={`card p-3 flex items-center gap-3 bg-gradient-to-r ${bg} text-white shadow-lg`}>
      <span className="text-2xl">{['🥇', '🥈', '🥉'][u.rank - 1]}</span>
      <Avatar src={u.image_url || u.avatar_url} name={u.username} size={46} square />
      <div className="flex-1 min-w-0">
        <p className="font-black truncate">{u.hero_name || u.username}</p>
        <p className="text-[11px] font-bold opacity-85">@{u.username} · L{u.level} · {u.wins}W</p>
      </div>
      <span className="font-black text-lg">{compact(u.score)}</span>
    </Link>
  );
}